import React, { useState, useEffect, useMemo } from "react";
import { PASS_MARK } from "../utils/constants";
import { safeArr } from "../utils/helpers";
import { PrimaryButton, SecondaryButton, Chip, Skeleton } from "./UI";
import { StatusPill } from "./StatusPill";

export function CategoryTable({
  items,
  summaries,
  loadingSummaries,
  onPractice,
  onFlash,
  onResults,
  onRefreshSummaries,
  isDarkMode,
}) {
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  const itemsLength = items?.length || 0;

  useEffect(() => {
    setQ("");
    setStatusFilter("all");
    setSortKey("name");
    setSortDir("asc");
  }, [itemsLength]);

  const rows = useMemo(() => {
    const list = safeArr(items).map((it) => {
      const s = summaries?.[it.label] || null;
      const stats =
        s?.stats || { total: 0, right: 0, wrong: 0, un: 0, accuracy: 0, status: "Unanswered" };
      return { ...it, stats };
    });

    const needle = q.trim().toLowerCase();
    let filtered = list;

    if (needle) filtered = filtered.filter((r) => String(r.label || "").toLowerCase().includes(needle));
    if (statusFilter !== "all") {
      filtered = filtered.filter((r) => String(r.stats?.status || "Unanswered").toLowerCase() === statusFilter);
    }

    const dir = sortDir === "asc" ? 1 : -1;
    filtered.sort((a, b) => {
      const A = a.stats || {};
      const B = b.stats || {};
      if (sortKey === "accuracy") return (Number(A.accuracy) - Number(B.accuracy)) * dir;
      if (sortKey === "wrong") return (Number(A.wrong) - Number(B.wrong)) * dir;
      if (sortKey === "unanswered") return (Number(A.un) - Number(B.un)) * dir;
      return String(a.label || "").localeCompare(String(b.label || ""), undefined, { sensitivity: "base" }) * dir;
    });

    return filtered;
  }, [items, summaries, q, statusFilter, sortKey, sortDir]);

  const overallStats = useMemo(() => {
    let totalAttempted = 0;
    let totalWrong = 0;
    let totalUnanswered = 0;

    for (const item of safeArr(items)) {
      const s = summaries?.[item.label];
      const stats = s?.stats || {};
      if (stats.total) {
        totalAttempted += Math.max(0, stats.total - stats.un);
        totalWrong += stats.wrong || 0;
        totalUnanswered += stats.un || 0;
      }
    }

    return { totalAttempted, totalWrong, totalUnanswered };
  }, [items, summaries]);

  console.log('[CategoryTable] Rendered. items:', items, 'summaries:', summaries, 'loadingSummaries:', loadingSummaries);
  return (
    <div className="px-6 py-6">
      <div className="mb-4 sm:mb-6 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3" data-tour="category-summary">
        <div className={`rounded-2xl border ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'} p-3 sm:p-4 shadow-sm`}>
          <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Attempted</div>
          <div className={`mt-1 text-xl sm:text-2xl font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{overallStats.totalAttempted}</div>
        </div>
        <div className={`rounded-2xl border ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'} p-3 sm:p-4 shadow-sm`}>
          <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Wrong</div>
          <div className="mt-1 text-xl sm:text-2xl font-semibold text-red-600">{overallStats.totalWrong}</div>
          {overallStats.totalWrong > 0 && (
            <button
              onClick={() => onPractice?.(null, "wrong")}
              className={`mt-2 text-xs ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'} hover:underline font-semibold`}
            >
              Practice Wrong
            </button>
          )}
        </div>
        <div className={`rounded-2xl border ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'} p-3 sm:p-4 shadow-sm`}>
          <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Unanswered</div>
          <div className={`mt-1 text-xl sm:text-2xl font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{overallStats.totalUnanswered}</div>
          {overallStats.totalUnanswered > 0 && (
            <button
              onClick={() => onPractice?.(null, "unanswered")}
              className={`mt-2 text-xs ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'} hover:underline font-semibold`}
            >
              Practice Unanswered
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4" data-tour="category-filters">
        <div className="flex w-full items-center gap-2 flex-wrap">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search category…"
            className={`w-full sm:w-[240px] rounded-xl border ${isDarkMode ? 'border-slate-600 bg-slate-700 text-white placeholder-slate-400 focus:ring-slate-500' : 'border-slate-200 bg-white focus:ring-indigo-200'} px-3 py-1.5 text-sm focus:outline-none focus:ring-2`}
          />

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={`flex-1 min-w-[130px] sm:flex-none rounded-xl border ${isDarkMode ? 'border-slate-600 bg-slate-700 text-white' : 'border-slate-200 bg-white'} px-3 py-1.5 text-sm`}
          >
            <option value="all">All statuses</option>
            <option value="pass">Pass</option>
            <option value="fail">Fail</option>
            <option value="unanswered">Unanswered</option>
          </select>

          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            className={`flex-1 min-w-[130px] sm:flex-none rounded-xl border ${isDarkMode ? 'border-slate-600 bg-slate-700 text-white' : 'border-slate-200 bg-white'} px-3 py-1.5 text-sm`}
          >
            <option value="name">Sort: Name</option>
            <option value="accuracy">Sort: Accuracy</option>
            <option value="wrong">Sort: Wrong</option>
            <option value="unanswered">Sort: Unanswered</option>
          </select>

          <button
            type="button"
            onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
            className={`shrink-0 rounded-xl border ${isDarkMode ? 'border-slate-600 bg-slate-700 text-white hover:bg-slate-600' : 'border-slate-200 bg-white hover:bg-slate-50'} px-3 py-1.5 text-sm font-medium`}
            title={`Sort categories ${sortDir === 'asc' ? 'descending' : 'ascending'}`}
          >
            {sortDir === "asc" ? "↑ Asc" : "↓ Desc"}
          </button>
        </div>

        <div className="w-full sm:w-auto flex">
          <SecondaryButton onClick={onRefreshSummaries} disabled={loadingSummaries} isDarkMode={isDarkMode} className="w-full sm:w-auto px-3 py-1.5 text-sm">
            {loadingSummaries ? "Refreshing…" : "Refresh status"}
          </SecondaryButton>
        </div>
      </div>

      <div
        className={`rounded-2xl border ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'} overflow-hidden`}
        data-tour="category-table"
      >
        <div className="block lg:hidden divide-y divide-slate-700/60">
          {rows.length === 0 ? (
            <div className={`px-4 py-6 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              No categories found.
            </div>
          ) : (
            rows.map((it) => {
              const st = it.stats || {};
              return (
                <div key={it.label} className="px-4 py-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{it.label}</div>
                      <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'} mt-0.5`}>
                        Practice questions, flashcards & results tracking
                      </div>
                    </div>
                    <div className="shrink-0">
                      {loadingSummaries ? <Skeleton className="h-7 w-28" /> : <StatusPill status={st.status} accuracy={st.accuracy} />}
                    </div>
                  </div>

                  <div className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-800'}`}>
                    <span className="font-semibold text-emerald-700">{st.right ?? 0}</span>
                    <span className={isDarkMode ? 'text-slate-500' : 'text-slate-400'}> / </span>
                    <span className="font-semibold text-red-700">{st.wrong ?? 0}</span>
                    <span className={isDarkMode ? 'text-slate-500' : 'text-slate-400'}> / </span>
                    <span className={`font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{st.un ?? 0}</span>
                    <span className={isDarkMode ? 'text-slate-500' : 'text-slate-400'}> • </span>
                    <span className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>
                      Total: <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{st.total ?? 0}</span>
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {it.isQuestions && (!it.videoUrl || it.videoUrl === "") ? (
                      <PrimaryButton
                        onClick={() => onPractice(it.label)}
                        title="Start practicing questions in this category"
                        data-tour="category-practice"
                        className="px-3 py-1.5 text-xs"
                      >
                        Practice
                      </PrimaryButton>
                    ) : (
                      <SecondaryButton disabled title="Questions not enabled for this category" className="px-3 py-1.5 text-xs">
                        Practice
                      </SecondaryButton>
                    )}

                    {it.isQuestions && (!it.videoUrl || it.videoUrl === "") && st.wrong > 0 && (
                      <SecondaryButton 
                        onClick={() => onPractice(it.label, "wrong")}
                        className="px-3 py-1.5 text-xs bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
                        title="Practice only the questions you answered incorrectly"
                      >
                        Wrong ({st.wrong})
                      </SecondaryButton>
                    )}

                    {it.isQuestions && (!it.videoUrl || it.videoUrl === "") && st.un > 0 && (
                      <SecondaryButton 
                        onClick={() => onPractice(it.label, "unanswered")}
                        className="px-3 py-1.5 text-xs bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                        title="Practice questions you haven't answered yet"
                      >
                        Unanswered ({st.un})
                      </SecondaryButton>
                    )}

                    {it.isFlash ? (
                      <button
                        type="button"
                        onClick={() => onFlash(it.label)}
                        className={`inline-flex items-center justify-center px-3 py-1.5 rounded-xl border text-xs font-medium transition ${isDarkMode ? 'border-slate-600 bg-slate-700 text-slate-100 hover:bg-slate-600' : 'border-slate-200 bg-white text-slate-800 hover:bg-slate-50'}`}
                        title="Review flashcards for this category"
                      >
                        Flashcards
                      </button>
                    ) : null}

                    <button
                      type="button"
                      onClick={() => onResults(it.label)}
                      className="inline-flex items-center justify-center px-3 py-1.5 rounded-xl text-xs font-semibold transition"
                      style={{ backgroundColor: "#4f46e5", color: "#ffffff" }}
                      title="View your results and performance for this category"
                    >
                      <span style={{ color: "#ffffff" }}>Results</span>
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-[980px] w-full">
            <thead className={`${isDarkMode ? 'bg-slate-900 border-b border-slate-700' : 'bg-slate-50 border-b border-slate-200'}`}>
              <tr className="text-left">
                <th className={`px-4 py-3 text-xs font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} uppercase tracking-wide`}>
                  Category
                </th>
                <th className={`px-4 py-3 text-xs font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} uppercase tracking-wide`}>
                  Status
                </th>
                <th className={`px-4 py-3 text-xs font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} uppercase tracking-wide`}>
                  Right / Wrong / Unanswered
                </th>
                <th className={`px-4 py-3 text-xs font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} uppercase tracking-wide`}>
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className={`${isDarkMode ? 'divide-slate-700' : 'divide-slate-100'} divide-y`}>
              {rows.length === 0 ? (
                <tr>
                  <td className={`px-4 py-6 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`} colSpan={4}>
                    No categories found.
                  </td>
                </tr>
              ) : (
                rows.map((it) => {
                  const st = it.stats || {};
                  return (
                    <tr key={it.label} className={`${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'}`}>
                      <td className="px-4 py-4">
                        <div className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{it.label}</div>
                        <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'} mt-0.5`}>
                          Practice questions, flashcards & results tracking
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        {loadingSummaries ? <Skeleton className="h-7 w-36" /> : <StatusPill status={st.status} accuracy={st.accuracy} />}
                      </td>

                      <td className="px-4 py-4">
                        {loadingSummaries ? (
                          <Skeleton className="h-5 w-56" />
                        ) : (
                          <div className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-800'}`}>
                            <span className="font-semibold text-emerald-700">{st.right ?? 0}</span>
                            <span className={isDarkMode ? 'text-slate-500' : 'text-slate-400'}> / </span>
                            <span className="font-semibold text-red-700">{st.wrong ?? 0}</span>
                            <span className={isDarkMode ? 'text-slate-500' : 'text-slate-400'}> / </span>
                            <span className={`font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{st.un ?? 0}</span>
                            <span className={isDarkMode ? 'text-slate-500' : 'text-slate-400'}> • </span>
                            <span className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>
                              Total: <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{st.total ?? 0}</span>
                            </span>
                          </div>
                        )}
                      </td>

                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-2">
                          {it.isQuestions && (!it.videoUrl || it.videoUrl === "") ? (
                            <PrimaryButton
                              onClick={() => onPractice(it.label)}
                              title="Start practicing questions in this category"
                              data-tour="category-practice"
                            >
                              Practice
                            </PrimaryButton>
                          ) : (
                            <SecondaryButton disabled title="Questions not enabled for this category">
                              Practice
                            </SecondaryButton>
                          )}

                          {it.isQuestions && (!it.videoUrl || it.videoUrl === "") && st.wrong > 0 && (
                            <SecondaryButton 
                              onClick={() => onPractice(it.label, "wrong")}
                              className="bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
                              title="Practice only the questions you answered incorrectly"
                            >
                              Wrong ({st.wrong})
                            </SecondaryButton>
                          )}

                          {it.isQuestions && (!it.videoUrl || it.videoUrl === "") && st.un > 0 && (
                            <SecondaryButton 
                              onClick={() => onPractice(it.label, "unanswered")}
                              className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                              title="Practice questions you haven't answered yet"
                            >
                              Unanswered ({st.un})
                            </SecondaryButton>
                          )}

                          {it.isFlash ? (
                            <button
                              type="button"
                              onClick={() => onFlash(it.label)}
                              className={`inline-flex items-center justify-center px-4 py-2 rounded-xl border text-sm font-medium transition ${isDarkMode ? 'border-slate-600 bg-slate-700 text-slate-100 hover:bg-slate-600' : 'border-slate-200 bg-white text-slate-800 hover:bg-slate-50'}`}
                              title="Review flashcards for this category"
                            >
                              Flashcards
                            </button>
                          ) : null}

                          <button
                            type="button"
                            onClick={() => onResults(it.label)}
                            className="inline-flex items-center justify-center px-4 py-2 rounded-xl text-sm font-semibold transition"
                            style={{ backgroundColor: "#4f46e5", color: "#ffffff" }}
                            title="View your results and performance for this category"
                          >
                            <span style={{ color: "#ffffff" }}>Results</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className={`mt-3 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
        Pass/Fail is based on latest attempt accuracy (Pass ≥ {PASS_MARK}%).
      </div>
    </div>
  );
}

